import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { novoRegistroBase } from '../../db/tipos'
import type { Produto, RegraComissao } from '../../db/tipos'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../../sync/outbox'
import {
  alvoDeFabricante, nomeDoAlvoFabricante, fabricantesDistintos, jaTemPercentualDeFabricante,
} from '../../comissoes/fabricante'
import { converterNumeroBR } from '../../lib/textoBR'
import { pendenciasDeComissao } from '../../comissoes/completude'
import { produtosComPrecoNaRegra } from '../../comissoes/produtosVendaveis'
import { numeroParaCampo } from '../../representadas/rascunhoDaPolitica'

const estiloCampo = 'border border-slate-300 rounded-lg px-2 py-1.5 text-sm'

export default function EditorPercentuais({
  regra,
  representadaId,
}: {
  regra: RegraComissao
  representadaId: string
}) {
  const [tipoAlvo, setTipoAlvo] = useState<'linha' | 'categoria' | 'produto' | 'fabricante'>('linha')
  const [erroAlvo, setErroAlvo] = useState<string | null>(null)
  const [textoAlvo, setTextoAlvo] = useState('')
  const [pctAlvo, setPctAlvo] = useState('')
  const [buscaProduto, setBuscaProduto] = useState('')
  const [produtoSelecionado, setProdutoSelecionado] = useState<string | null>(null)
  // null = o formulario abaixo esta ADICIONANDO um percentual novo; com id,
  // esta EDITANDO aquele -- salva por UPDATE no mesmo id (apagar-e-recriar
  // perderia o vinculo se um pedido antigo ja tivesse sido calculado com ele,
  // ainda que este alvo especifico so afete pedidos futuros).
  const [editandoId, setEditandoId] = useState<string | null>(null)

  const percentuais = useLiveQuery(
    () =>
      banco.comissao_percentuais
        .where('regra_id')
        .equals(regra.id)
        .filter((p) => p.apagado_em === null)
        .toArray(),
    [regra.id],
  )
  // Defensivo: se o percentual em edicao sumir (apagado em outro aparelho
  // enquanto esta tela estava aberta), sai do modo edicao em vez de deixar o
  // "Salvar" gravando por cima de um id que nao existe mais.
  useEffect(() => {
    if (editandoId && percentuais && !percentuais.some((p) => p.id === editandoId)) {
      limparFormulario()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentuais, editandoId])
  const produtos = useLiveQuery(
    () =>
      buscaProduto.length >= 2
        ? banco.produtos
            .where('representada_id')
            .equals(representadaId)
            .filter(
              (p) =>
                p.apagado_em === null &&
                (p.codigo.toLowerCase().includes(buscaProduto.toLowerCase()) ||
                  p.descricao.toLowerCase().includes(buscaProduto.toLowerCase())),
            )
            .limit(8)
            .toArray()
        : Promise.resolve<Produto[]>([]),
    [buscaProduto, representadaId],
  )
  const todosProdutos = useLiveQuery(
    () => banco.produtos.where('representada_id').equals(representadaId).toArray(),
    [representadaId],
  )
  const produtosVendaveis = useLiveQuery(
    () => produtosComPrecoNaRegra(representadaId, regra),
    [representadaId, regra.id, regra.tabela_preco_id, regra.tipo],
  )
  const totalProdutos = useLiveQuery(
    () =>
      banco.produtos
        .where('representada_id')
        .equals(representadaId)
        .filter((p) => p.apagado_em === null)
        .count(),
    [representadaId],
  )
  // valores distintos de linha/categoria dos produtos da representada (para o seletor)
  const valoresAlvo = useLiveQuery(async () => {
    const prods = await banco.produtos
      .where('representada_id')
      .equals(representadaId)
      .filter((p) => p.apagado_em === null)
      .toArray()
    const distintos = (sel: (p: Produto) => string | null) =>
      [...new Set(prods.map(sel).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b))
    return {
      linhas: distintos((p) => p.linha),
      categorias: distintos((p) => p.categoria),
      // Junta grafias diferentes numa entrada so -- duas linhas na lista fariam
      // ela criar duas regras achando que sao fabricantes distintos.
      fabricantes: fabricantesDistintos(prods),
    }
  }, [representadaId])

  // Limpa so o formulario de baixo -- tipoAlvo fica como esta, de proposito
  // (o molde de sempre: depois de adicionar um produto, o seletor continua em
  // "Produto" para o proximo, em vez de voltar para "Linha").
  function limparFormulario() {
    setTextoAlvo('')
    setPctAlvo('')
    setBuscaProduto('')
    setProdutoSelecionado(null)
    setErroAlvo(null)
    setEditandoId(null)
  }

  function editar(p: { id: string; alvo: string; pct: number }) {
    setErroAlvo(null)
    // Fix round 1 do Task 13: era String(p.pct) -- "12.125" volta do
    // converterNumeroBR como DOZE MIL (ponto = milhar), corrompendo o % ao
    // tocar Salvar sem mudar nada. numeroParaCampo devolve "12,125".
    setPctAlvo(numeroParaCampo(p.pct))
    if (p.alvo.startsWith('produto:')) {
      const produtoId = p.alvo.slice('produto:'.length)
      setTipoAlvo('produto')
      setProdutoSelecionado(produtoId)
      setBuscaProduto(todosProdutos?.find((pr) => pr.id === produtoId)?.descricao ?? '')
      setTextoAlvo('')
    } else {
      const fabricante = nomeDoAlvoFabricante(p.alvo)
      setBuscaProduto('')
      setProdutoSelecionado(null)
      if (fabricante !== null) {
        setTipoAlvo('fabricante')
        setTextoAlvo(fabricante)
      } else if (p.alvo.startsWith('linha:')) {
        setTipoAlvo('linha')
        setTextoAlvo(p.alvo.slice('linha:'.length))
      } else if (p.alvo.startsWith('categoria:')) {
        setTipoAlvo('categoria')
        setTextoAlvo(p.alvo.slice('categoria:'.length))
      }
    }
    setEditandoId(p.id)
  }

  async function salvar() {
    const pct = converterNumeroBR(pctAlvo)
    if (pct === null || pct < 0) return
    const alvo =
      tipoAlvo === 'produto'
        ? produtoSelecionado
          ? `produto:${produtoSelecionado}`
          : null
        : textoAlvo.trim()
          ? tipoAlvo === 'fabricante'
            ? alvoDeFabricante(textoAlvo)
            : `${tipoAlvo}:${textoAlvo.trim()}`
          : null
    if (!alvo) return
    // ⚠ So o fabricante casa NORMALIZADO. Duas grafias do mesmo fabricante na
    // MESMA regra casariam com o mesmo produto, e qual venceria seria a ordem
    // do `find` -- sorteio pagando dinheiro. Acontece de verdade: ela cria a
    // regra com a grafia de hoje e a fabrica manda a planilha seguinte com
    // outra. Entre regras diferentes nao ha conflito -- escolherRegra ja decide
    // qual regra vale para a tabela.
    if (tipoAlvo === 'fabricante') {
      const nome = textoAlvo.trim()
      // exclui o proprio item em edicao: senao editar SO o percentual de um
      // fabricante (mantendo o mesmo nome) se recusaria por "ja existe".
      const outros = (percentuais ?? []).filter((p) => p.id !== editandoId)
      if (jaTemPercentualDeFabricante(outros, nome)) {
        setErroAlvo(`Esta regra já tem um percentual para o fabricante "${nome}".`)
        return
      }
    }
    setErroAlvo(null)
    const existente = editandoId ? percentuais?.find((p) => p.id === editandoId) : undefined
    await gravarLocalEEnfileirar('comissao_percentuais', {
      ...(existente ?? { ...novoRegistroBase(), regra_id: regra.id }),
      alvo,
      pct,
    })
    limparFormulario()
  }

  function rotuloAlvo(alvo: string): string {
    if (alvo.startsWith('produto:')) {
      const produto = todosProdutos?.find((p) => p.id === alvo.slice('produto:'.length))
      return `Produto: ${produto?.descricao ?? '(não sincronizado)'}`
    }
    // ⚠ ANTES do `replace` de baixo: aquele replace devolveria
    // `fabricante:MAURI` intacto, sem erro nenhum -- falha silenciosa, e esta e
    // a tela onde ela confere o que configurou.
    const fabricante = nomeDoAlvoFabricante(alvo)
    if (fabricante !== null) return `Fabricante: ${fabricante}`
    return alvo.replace('linha:', 'Linha: ').replace('categoria:', 'Categoria: ')
  }

  const opcoesAlvo =
    tipoAlvo === 'linha' ? valoresAlvo?.linhas
      : tipoAlvo === 'fabricante' ? valoresAlvo?.fabricantes
        : valoresAlvo?.categorias

  return (
    <div className="space-y-2 border-t border-slate-100 pt-2" data-testid="percentuais-especificos">
      <h5 className="text-sm text-slate-600">Percentuais específicos</h5>
      {(() => {
        const pendentes = pendenciasDeComissao(regra, percentuais ?? [], produtosVendaveis ?? [])
        if (pendentes.length === 0) return null
        const fallback = regra.pct_padrao ?? 0
        return (
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-2">
            ⚠️ {pendentes.length} produto(s) com preço nesta tabela e sem comissão própria —{' '}
            {fallback > 0
              ? `usarão o padrão de ${fallback}%.`
              : 'calcularão 0% até você definir.'}
          </p>
        )
      })()}
      <ul className="space-y-1">
        {percentuais?.map((p) => (
          <li key={p.id} className="flex items-center justify-between text-sm text-slate-700">
            <span>
              {rotuloAlvo(p.alvo)} — <strong>{p.pct}%</strong>
            </span>
            <span className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => editar(p)}
                className="text-teal-700 text-xs underline"
              >
                editar
              </button>
              <button
                type="button"
                onClick={() => void apagarLocalEEnfileirar('comissao_percentuais', p.id)}
                className="text-red-600 text-xs underline"
              >
                remover
              </button>
            </span>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2 items-start">
        <select
          aria-label="Tipo de alvo"
          value={tipoAlvo}
          onChange={(e) => {
            setTipoAlvo(e.target.value as typeof tipoAlvo)
            setErroAlvo(null)
            setTextoAlvo('')
            setBuscaProduto('')
            setProdutoSelecionado(null)
          }}
          className={estiloCampo}
        >
          <option value="linha">Linha</option>
          <option value="fabricante">Fabricante</option>
          <option value="categoria">Categoria</option>
          <option value="produto">Produto</option>
        </select>

        {tipoAlvo === 'produto' ? (
          <div className="flex-1 min-w-40">
            <input
              value={buscaProduto}
              onChange={(e) => {
                setBuscaProduto(e.target.value)
                setProdutoSelecionado(null)
              }}
              placeholder="Buscar produto…"
              className={`${estiloCampo} w-full`}
            />
            {buscaProduto.length < 2 && !produtoSelecionado && (
              <p className="text-xs text-slate-400 mt-1">digite ao menos 2 letras</p>
            )}
            {buscaProduto.length >= 2 && !produtoSelecionado && (
              produtos === undefined ? (
                <p className="text-xs text-slate-400 mt-1">buscando…</p>
              ) : produtos.length === 0 ? (
                (totalProdutos ?? 0) === 0 ? (
                  <p className="text-xs text-amber-600 mt-1">
                    essa fornecedora ainda não tem produtos sincronizados
                  </p>
                ) : (
                  <p className="text-xs text-slate-400 mt-1">nenhum produto encontrado</p>
                )
              ) : (
                <ul className="border border-slate-200 rounded-lg divide-y mt-1">
                  {produtos.map((p) => (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setProdutoSelecionado(p.id)
                          setBuscaProduto(p.descricao)
                        }}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        {p.codigo} — {p.descricao}
                      </button>
                    </li>
                  ))}
                </ul>
              )
            )}
          </div>
        ) : (
          <div className="flex-1 min-w-40">
            <select
              aria-label="Valor do alvo"
              value={textoAlvo}
              onChange={(e) => setTextoAlvo(e.target.value)}
              className={`${estiloCampo} w-full`}
            >
              <option value="">
                {tipoAlvo === 'linha' ? 'Escolha a linha…'
                  : tipoAlvo === 'fabricante' ? 'Escolha o fabricante…'
                    : 'Escolha a categoria…'}
              </option>
              {opcoesAlvo?.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            {opcoesAlvo !== undefined && opcoesAlvo.length === 0 && (
              tipoAlvo === 'fabricante' ? (
                <p className="text-xs text-amber-600 mt-1">
                  nenhum fabricante nos produtos desta fornecedora — o fabricante entra pela coluna
                  FAB da planilha, ou pela ficha de um produto
                </p>
              ) : (
                <p className="text-xs text-amber-600 mt-1">
                  nenhuma {tipoAlvo === 'linha' ? 'linha' : 'categoria'} nos produtos desta fornecedora
                </p>
              )
            )}
            {erroAlvo && <p className="text-xs text-red-600 mt-1">{erroAlvo}</p>}
          </div>
        )}

        <input
          aria-label="Percentual do alvo"
          value={pctAlvo}
          onChange={(e) => setPctAlvo(e.target.value)}
          placeholder="%"
          inputMode="decimal"
          className={`${estiloCampo} w-16`}
        />
        <button
          type="button"
          onClick={() => void salvar()}
          className="bg-teal-700 text-white rounded-lg px-3 py-1.5 text-sm"
        >
          {editandoId ? 'Salvar' : 'Adicionar'}
        </button>
        {editandoId && (
          <button
            type="button"
            onClick={limparFormulario}
            className="text-slate-600 text-sm underline"
          >
            Cancelar
          </button>
        )}
      </div>
    </div>
  )
}
